import type {
    CreateOrganizationContactRequestBody,
    UpdateOrganizationContactRequestBody,
} from '@engagement-web-app/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, type ErrorComponentProps, useRouter } from '@tanstack/react-router';
import { Briefcase, Globe, type LucideIcon, MapPinned, UserCheck } from 'lucide-react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Link } from '@/components/Link';
import { Section, SectionHeader } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { showErrorToast, showSuccessToast } from '@/components/Toasts';
import {
    createOrganizationContact,
    deleteOrganizationContact,
    updateOrganizationContact,
} from '@/queries/organization-contacts';
import { fetchOrganization } from '@/queries/organizations';
import { getFullName } from '@/utils';
import { DeleteContactDialog } from '../-fragments/DeleteContactDialog';
import { EditAssignedStaffDialog } from '../-fragments/EditAssignedStaffDialog';
import { EditContactDialog } from '../-fragments/EditContactDialog';
import { EditOrganizationDetailDialog } from '../-fragments/EditOrganizationDetailDialog';
import { PrimaryContactSection, PrimaryContactSectionSkeleton } from '../-fragments/PrimaryContactSection';
import { useUpdateOrganization } from '../-hooks';
import { formatOrganizationAddressText } from '../-utils/name-formatter';
import * as styles from '../route.css';

export const Route = createFileRoute('/system/_auth/organizations/$organizationId/profile/')({
    loader: async ({ params }) => {
        const { organizationId } = params;
        return fetchOrganization({ uuid: organizationId });
    },
    pendingComponent: SystemShowOrganizationProfileRouteSkeleton,
    errorComponent: SystemShowOrganizationProfileRouteError,
    component: SystemShowOrganizationProfileRoute,
});

function SystemShowOrganizationProfileRouteError({ reset }: ErrorComponentProps) {
    return <ErrorOverlay active onRetry={reset} />;
}

function SystemShowOrganizationProfileRoute() {
    const org = Route.useLoaderData();
    const router = useRouter();
    const queryClient = useQueryClient();

    const updateInfoMutation = useUpdateOrganization(org.uuid, 'info', () => router.invalidate());

    const updateAssignedStaffMutation = useUpdateOrganization(org.uuid, 'assignedStaff', () => router.invalidate());

    const createContactMutation = useMutation({
        mutationFn: (body: CreateOrganizationContactRequestBody) => createOrganizationContact(org.uuid, body),
        onSuccess() {
            queryClient.invalidateQueries({ queryKey: ['organizationContact', org.uuid] });
            router.invalidate();
            showSuccessToast({ title: '連絡先追加', description: '連絡先が追加されました。' });
        },
        onError(error) {
            console.error('Failed to create contact:', error);
            showErrorToast({ title: '連絡先追加', description: '連絡先の追加に失敗しました。' });
        },
    });

    const editContactMutation = useMutation({
        mutationFn: (body: UpdateOrganizationContactRequestBody) => updateOrganizationContact(org.uuid, body),
        onSuccess() {
            queryClient.invalidateQueries({ queryKey: ['organizationContact', org.uuid] });
            router.invalidate();
            showSuccessToast({ title: '連絡先編集', description: '連絡先が更新されました。' });
        },
        onError(error) {
            console.error('Failed to edit contact:', error);
            showErrorToast({ title: '連絡先編集', description: '連絡先の更新に失敗しました。' });
        },
    });

    const deleteContactMutation = useMutation({
        mutationFn: () => deleteOrganizationContact(org.uuid),
        onSuccess() {
            queryClient.invalidateQueries({ queryKey: ['organizationContact', org.uuid] });
            router.invalidate();
            showSuccessToast({ title: '連絡先削除', description: '連絡先が削除されました。' });
        },
        onError(error) {
            console.error('Failed to delete contact:', error);
            showErrorToast({ title: '連絡先削除', description: '連絡先の削除に失敗しました。' });
        },
    });

    const { contact } = org;

    return (
        <>
            <Section>
                <SectionHeader
                    title="企業情報"
                    extras={
                        <Flex justify="end">
                            <EditOrganizationDetailDialog
                                org={org}
                                isSubmitting={updateInfoMutation.isPending}
                                onSubmit={updateInfoMutation.mutateAsync}
                            />
                        </Flex>
                    }
                    description="クライアントの企業のウェブサイトと住所です。"
                />

                <Flex as="dl" direction="column" gap="3">
                    <Flex align="center" gap="2">
                        <Icon icon={Globe} size="sm" className={styles.icon} />
                        <div>
                            <dt className={styles.fieldLabel}>ホームページ</dt>
                            <dd className={styles.fieldValue}>
                                {org.website ? (
                                    <Link href={org.website} target="_blank" rel="noopener noreferrer">
                                        {org.website}
                                    </Link>
                                ) : (
                                    <span className={styles.empty}>未設定</span>
                                )}
                            </dd>
                        </div>
                    </Flex>
                    <Flex align="center" gap="2">
                        <Icon icon={MapPinned} size="sm" className={styles.icon} />
                        <div>
                            <dt className={styles.fieldLabel}>住所</dt>
                            <dd className={styles.fieldValue}>{formatOrganizationAddressText(org)}</dd>
                        </div>
                    </Flex>
                </Flex>
            </Section>

            <Section>
                <SectionHeader
                    title="担当者"
                    extras={
                        <Flex justify="end">
                            <EditAssignedStaffDialog
                                org={org}
                                isSubmitting={updateAssignedStaffMutation.isPending}
                                onSubmit={updateAssignedStaffMutation.mutateAsync}
                            />
                        </Flex>
                    }
                    description="クライアントの担当者です。"
                />

                <Flex as="dl" direction="column" gap="3">
                    <Flex align="center" gap="2">
                        <Icon icon={UserCheck} size="sm" className={styles.icon} />
                        <div>
                            <dt className={styles.fieldLabel}>コンサルタント担当</dt>
                            <dd className={styles.fieldValue}>
                                {org.assignedConsultant ? (
                                    getFullName(org.assignedConsultant)
                                ) : (
                                    <span className={styles.empty}>未設定</span>
                                )}
                            </dd>
                        </div>
                    </Flex>
                    <Flex align="center" gap="2">
                        <Icon icon={Briefcase} size="sm" className={styles.icon} />
                        <div>
                            <dt className={styles.fieldLabel}>営業部門担当</dt>
                            <dd className={styles.fieldValue}>
                                {org.assignedSales ?? <span className={styles.empty}>未設定</span>}
                            </dd>
                        </div>
                    </Flex>
                </Flex>
            </Section>

            <Section>
                <SectionHeader
                    title="連絡先"
                    extras={
                        <Flex justify="end" align="center" gap="2">
                            {contact && (
                                <DeleteContactDialog
                                    contact={contact}
                                    isSubmitting={deleteContactMutation.isPending}
                                    onSubmit={deleteContactMutation.mutateAsync}
                                />
                            )}
                            <EditContactDialog
                                contact={contact}
                                isSubmitting={contact ? editContactMutation.isPending : createContactMutation.isPending}
                                onSubmit={contact ? editContactMutation.mutateAsync : createContactMutation.mutateAsync}
                            />
                        </Flex>
                    }
                    description="クライアント側の窓口・担当者の連絡先です。"
                />

                <PrimaryContactSection contact={contact} />
            </Section>
        </>
    );
}

function SystemShowOrganizationProfileRouteSkeleton() {
    return (
        <>
            <Section>
                <SectionHeader title="企業情報" description="クライアントの企業のウェブサイトと住所です。" />

                <Flex as="dl" direction="column" gap="3">
                    <FieldRowSkeleton icon={Globe} label="ホームページ" />
                    <FieldRowSkeleton icon={MapPinned} label="住所" />
                </Flex>
            </Section>

            <Section>
                <SectionHeader title="担当者" description="クライアントの担当者です。" />

                <Flex as="dl" direction="column" gap="3">
                    <FieldRowSkeleton icon={UserCheck} label="コンサルタント担当" />
                    <FieldRowSkeleton icon={Briefcase} label="営業部門担当" />
                </Flex>
            </Section>

            <Section>
                <SectionHeader title="連絡先" description="クライアント側の窓口・担当者の連絡先です。" />

                <PrimaryContactSectionSkeleton />
            </Section>
        </>
    );
}

type FieldRowSkeletonProps = {
    icon: LucideIcon;
    label: string;
};

function FieldRowSkeleton({ icon, label }: FieldRowSkeletonProps) {
    return (
        <Flex align="center" gap="2">
            <Icon icon={icon} size="sm" className={styles.icon} />
            <div>
                <dt className={styles.fieldLabel}>{label}</dt>
                <dd className={styles.fieldValue}>
                    <Skeleton.Line width={160} height="sm" />
                </dd>
            </div>
        </Flex>
    );
}
